import React from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import AspectRatioSelector, { AspectRatioOption } from './AspectRatioSelector';

interface CropStepProps {
  image: string | null;
  crop: Crop;
  completedCrop: PixelCrop | null;
  currentAspectRatio: number;
  aspectRatios: AspectRatioOption[];
  selectedAspectRatioId: number | null;
  onAspectRatioChange: (aspectRatioId: number, ratio: number) => void;
  onCropChange: (crop: Crop) => void;
  onCropComplete: (crop: PixelCrop) => void;
  onImageLoaded: (img: HTMLImageElement) => boolean;
  imgRef?: React.RefObject<HTMLImageElement>;
}

const CropStep: React.FC<CropStepProps> = ({
  image,
  crop,
  completedCrop,
  currentAspectRatio,
  aspectRatios,
  selectedAspectRatioId,
  onAspectRatioChange,
  onCropChange,
  onCropComplete,
  onImageLoaded,
  imgRef
}) => {
  return (
    <div className="wizard-step crop-step">
      <h2>Crop Your Image</h2>
      <p className="step-description">
        First, choose an aspect ratio for your crop. Then adjust the crop area to select the perfect portion of your image.
      </p>
      
      {image ? (
        <div className="crop-wrapper">
          {/* Aspect Ratio Selector */}
          <div className="aspect-ratio-section">
            <AspectRatioSelector
              aspectRatios={aspectRatios}
              selectedAspectRatioId={selectedAspectRatioId}
              onAspectRatioChange={onAspectRatioChange}
            />
          </div>

          {/* Crop Area */}
          {selectedAspectRatioId && (
            <div className="crop-area-section">
              <div className="crop-instructions">
                <i className="fas fa-info-circle"></i>
                <span>Drag the corners or edges to adjust your crop. The aspect ratio is locked to {currentAspectRatio.toFixed(2)}:1</span>
              </div>
              
              <div className="crop-container">
                <ReactCrop
                  crop={crop}
                  onChange={onCropChange}
                  onComplete={onCropComplete}
                  aspect={currentAspectRatio}
                  key={`crop-${selectedAspectRatioId}-${currentAspectRatio}`}
                >
                  <img 
                    ref={imgRef}
                    src={image} 
                    alt="Crop" 
                    onLoad={(e) => onImageLoaded(e.currentTarget)}
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      width: 'auto',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </ReactCrop>
              </div>

              {completedCrop && (
                <div className="crop-success-message">
                  <i className="fas fa-check-circle"></i>
                  <span>Crop area selected! You can continue adjusting or proceed to size selection.</span>
                </div>
              )}
            </div>
          )}

          {!selectedAspectRatioId && (
            <div className="crop-prompt">
              <i className="fas fa-hand-pointer"></i>
              <p>Please select an aspect ratio above to begin cropping your image.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="crop-error-state">
          <p className="error-text">Please go back to Step 1 and upload an image first</p>
        </div>
      )}
    </div>
  );
};

export default CropStep;
